import { DEFAULT_BRAND_LOGO_URL } from "../../remotion/brand/logo";
import { hexToAssColor, type CaptionStyle } from "./clip-plan.logic";

/**
 * Brand identity of a rendered clip (TRE-1979) — the one-off equivalent of the ACQ kit's
 * "brand interview", pinned in code so every clip looks the same. Values are overridable
 * via env so marketing tweaks (font, colours, caption size) do not need a code change.
 *
 * Colours are plain hex; the ASS fallback converts them to &HAABBGGRR itself.
 */

export type CaptionEngine = "remotion" | "ass";

export interface ClipBrandConfig {
  /**
   * remotion (default, decyzja z TRE-1975): napisy renderowane komponentem React —
   * spójny wygląd i pełna kontrola nad grafiką. ass: awaryjne wypalanie przez
   * ffmpeg/libass, używane też automatycznie, gdy render Remotion się nie powiedzie.
   */
  captionEngine: CaptionEngine;
  fontFamily: string;
  fontSize: number;
  /** Aktualnie wypowiadane słowo. */
  highlightColor: string;
  baseColor: string;
  outlineColor: string;
  /** Odległość napisów od dołu kadru — ponad overlayem UI Reels/TikToka. */
  marginBottom: number;
  maxCharsPerLine: number;
  maxLines: number;
  /** Słów na stronę napisów (etap B: 2–3, styl „spice"). */
  maxWordsPerPage: number;
  /** Strona krótsza niż to jest sklejana z sąsiednią. */
  minPageMs: number;
  /** Wagi fontu zmiennego: baza / mocne słowo / puenta. */
  weightBase: number;
  weightStrong: number;
  weightPayoff: number;
  targetWidth: number;
  targetHeight: number;
  /**
   * face (default, etap A): crop 9:16 that follows the speaker, split-screen for two
   * people, full picture with bars only when no face can be trusted. pad: the pre-etap-A
   * look — the whole source image fitted into 9:16 (kill switch, no detector run).
   */
  framingMode: "face" | "pad";
  /** Tightest crop relative to the full-height crop; 1080p sources cannot take more upscaling. */
  framingMaxZoom: number;
  /** on (default, etap C1): denoise + EQ + compressor + limiter + loudnorm -14 LUFS; off: source audio as is. */
  audioChain: "on" | "off";
  audioTargetLufs: number;
  /** on (default, etap C2): mix a bed from Studio → Muzyka when the library has one; off: never. */
  music: "on" | "off";
  /** Bed level relative to the voice before loudnorm; the kit keeps beds 16–20 LU under speech. */
  musicGainDb: number;
  /** Prefer beds of this vibe when the library has several; null = any. */
  musicVibe: "calm" | "energetic" | "story" | null;
  /** Logo in the top-right safe zone (etap E); null = no logo. Default: the embedded brand mark. */
  logoUrl: string | null;
  /** Outro appended after the last word (etap E); null = none. Set per environment by marketing. */
  outro: { url: string; kind: "image" | "video"; durationMs: number } | null;
}

/**
 * Defaults shared with the in-panel preview (etap F1), which has no process.env: the
 * browser must page and style captions exactly like the server does.
 */
export const DEFAULT_CLIP_BRAND: ClipBrandConfig = {
  captionEngine: "remotion",
  // Montserrat (OFL) rides inside the Remotion bundle (src/remotion/fonts); a brand font
  // lands here once marketing hands one over. The libass fallback keeps DejaVu Sans, which
  // ships with the Debian image.
  fontFamily: "Montserrat Clip",
  fontSize: 78,
  highlightColor: "#FFD200",
  baseColor: "#FFFFFF",
  outlineColor: "#000000",
  marginBottom: 260,
  maxCharsPerLine: 18,
  maxLines: 1,
  maxWordsPerPage: 3,
  minPageMs: 400,
  weightBase: 500,
  weightStrong: 700,
  weightPayoff: 900,
  targetWidth: 1080,
  targetHeight: 1920,
  framingMode: "face",
  framingMaxZoom: 1.4,
  audioChain: "on",
  audioTargetLufs: -14,
  music: "on",
  musicGainDb: -16,
  musicVibe: null,
  logoUrl: DEFAULT_BRAND_LOGO_URL,
  outro: null,
};

/** CLIP_BRAND_OUTRO_URL=<https://…mp4|png|jpg> [CLIP_BRAND_OUTRO_MS=1500] → outro descriptor. */
export const parseOutroEnv = (url: string | undefined, ms: string | undefined): ClipBrandConfig["outro"] => {
  if (!url) return null;
  const durationMs = Number(ms) || 1_500;
  const kind = /\.(mp4|mov|webm)(\?|$)/i.test(url) ? "video" : "image";
  return { url, kind, durationMs };
};

export const getClipBrandConfig = (): ClipBrandConfig => ({
  ...DEFAULT_CLIP_BRAND,
  captionEngine: process.env.CLIP_CAPTION_ENGINE === "ass" ? "ass" : "remotion",
  fontFamily: process.env.CLIP_CAPTION_FONT || DEFAULT_CLIP_BRAND.fontFamily,
  fontSize: Number(process.env.CLIP_CAPTION_FONT_SIZE) || DEFAULT_CLIP_BRAND.fontSize,
  highlightColor: process.env.CLIP_CAPTION_HIGHLIGHT_COLOUR || DEFAULT_CLIP_BRAND.highlightColor,
  baseColor: process.env.CLIP_CAPTION_BASE_COLOUR || DEFAULT_CLIP_BRAND.baseColor,
  marginBottom: Number(process.env.CLIP_CAPTION_MARGIN_V) || DEFAULT_CLIP_BRAND.marginBottom,
  framingMode: process.env.CLIP_FRAMING_MODE === "pad" ? "pad" : "face",
  framingMaxZoom: Number(process.env.CLIP_FRAMING_MAX_ZOOM) || DEFAULT_CLIP_BRAND.framingMaxZoom,
  audioChain: process.env.CLIP_AUDIO_CHAIN === "off" ? "off" : "on",
  audioTargetLufs: Number(process.env.CLIP_AUDIO_TARGET_LUFS) || DEFAULT_CLIP_BRAND.audioTargetLufs,
  music: process.env.CLIP_MUSIC === "off" ? "off" : "on",
  musicGainDb: Number(process.env.CLIP_MUSIC_GAIN_DB) || DEFAULT_CLIP_BRAND.musicGainDb,
  musicVibe: (["calm", "energetic", "story"] as const).find((vibe) => vibe === process.env.CLIP_MUSIC_VIBE) ?? null,
  logoUrl: process.env.CLIP_BRAND_LOGO === "off" ? null : process.env.CLIP_BRAND_LOGO_URL || DEFAULT_CLIP_BRAND.logoUrl,
  outro: parseOutroEnv(process.env.CLIP_BRAND_OUTRO_URL, process.env.CLIP_BRAND_OUTRO_MS),
});

export type CaptionAnchor = "bottom" | "center";

/**
 * Where the captions sit follows the picture: on a face-tracked crop they go mid-frame (the
 * kit's ~50% anchor, on the speaker's chest, never over the eyes); on the padded full picture
 * they stay in the lower third below the image.
 */
export const captionAnchorForFraming = (mode: "single" | "split" | "pad" | null | undefined): CaptionAnchor =>
  mode === "single" || mode === "split" ? "center" : "bottom";

/** Style props of the Remotion caption layer, derived from the brand the same way everywhere. */
export const toCaptionedClipStyle = (brand: ClipBrandConfig, anchor: CaptionAnchor = "bottom") => ({
  fontFamily: `"${brand.fontFamily}", Montserrat, Arial, Helvetica, sans-serif`,
  fontSize: brand.fontSize,
  highlightColor: brand.highlightColor,
  baseColor: brand.baseColor,
  outlineColor: brand.outlineColor,
  marginBottom: brand.marginBottom,
  anchor,
  weightBase: brand.weightBase,
  weightStrong: brand.weightStrong,
  weightPayoff: brand.weightPayoff,
  maxWidthRatio: 0.82,
  shadow: "soft" as const,
  dropInPx: 8,
  dropInMs: 500,
});

/** Paging options of the caption planner, from the same brand values. */
export const toCaptionPagingOptions = (brand: ClipBrandConfig) => ({
  maxCharsPerLine: brand.maxCharsPerLine,
  maxLines: brand.maxLines,
  maxWordsPerPage: brand.maxWordsPerPage,
  breakOnClauses: true,
  minPageMs: brand.minPageMs,
});

/** ASS style for the ffmpeg/libass fallback path, derived from the same brand values. */
export const toAssCaptionStyle = (brand: ClipBrandConfig): CaptionStyle => ({
  // The inlined Montserrat is a browser font; libass gets the image's DejaVu Sans.
  fontName: "DejaVu Sans",
  fontSize: brand.fontSize,
  primaryColour: hexToAssColor(brand.highlightColor),
  secondaryColour: hexToAssColor(brand.baseColor),
  outlineColour: hexToAssColor(brand.outlineColor),
  backColour: "&H80000000",
  outline: 5,
  shadow: 0,
  marginV: brand.marginBottom,
  bold: true,
});
